import { CheckCircle2, Phone, MessageCircle, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { ASSETS, BUSINESS, PIAGGIO_HIGHLIGHTS, KYMCO_HIGHLIGHTS } from "../lib/data";

export default function Scooter() {
  return (
    <>
      <SEO
        title="Rivenditore Ufficiale Piaggio e Kymco Roma | Il Velocipede"
        description="Rivenditore ufficiale Piaggio e Kymco a Roma. Scooter nuovi, preventivi personalizzati e permute nel quartiere Nuovo Salario - Montesacro. Dal 1973."
      />

      {/* HERO */}
      <section className="relative bg-white border-b border-slate-200" data-testid="scooter-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline">Rivenditore Ufficiale</div>
            <div className="divider-red mt-3 mb-6" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05]">
              Rivenditore Ufficiale <span className="text-[#013368]">Piaggio</span> e <span className="text-[#fd0100]">Kymco</span> a Roma
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Scopri tutta la gamma scooter Piaggio e Kymco. Vendita, preventivi personalizzati, permute e finanziamenti. Dal 1973, il punto di riferimento per chi cerca uno scooter a Roma Nord, Nuovo Salario e Montesacro.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`https://wa.me/${BUSINESS.whatsappRaw}?text=Salve%2C%20vorrei%20un%20preventivo%20per%20uno%20scooter`} target="_blank" rel="noopener noreferrer" data-testid="scooter-cta-preventivo" className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm shadow-lg shadow-red-900/20">
                <MessageCircle className="w-4 h-4" /> Richiedi Preventivo
              </a>
              <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="scooter-cta-call" className="btn-outline inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm">
                <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4 items-center">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 aspect-square flex items-center justify-center">
                <img src={ASSETS.piaggio} alt="Rivenditore Ufficiale Piaggio Roma" className="max-w-full max-h-full object-contain" />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 aspect-square flex items-center justify-center">
                <img src={ASSETS.kymco} alt="Rivenditore Ufficiale Kymco Roma" className="max-w-full max-h-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PIAGGIO */}
      <section className="py-20 md:py-28 bg-white" data-testid="piaggio-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                <img src={ASSETS.piaggio} alt="Piaggio" className="w-14 h-14 object-contain" />
              </div>
              <div>
                <div className="overline">Rivenditore Ufficiale</div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Scooter Piaggio Roma</h2>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed">
              Da oltre 50 anni rappresentiamo il marchio Piaggio a Roma Nord. Vespa, Liberty, Beverly, Medley, MP3: ogni modello della gamma Piaggio è disponibile su ordinazione con consegna rapida, configurazione personalizzata, assistenza tecnica autorizzata e <strong>ricambi originali</strong>.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Se cerchi uno <strong>scooter Piaggio a Roma</strong>, scrivici su WhatsApp o passa in negozio: ti aiutiamo a scegliere il modello giusto, ti facciamo un preventivo trasparente e valutiamo la tua permuta.
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Gamma Piaggio disponibile</h3>
            <ul className="space-y-3">
              {PIAGGIO_HIGHLIGHTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-[#013368] shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* KYMCO */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="kymco-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div className="order-2 lg:order-1">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Gamma Kymco disponibile</h3>
            <ul className="space-y-3">
              {KYMCO_HIGHLIGHTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-[#fd0100] shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-sm">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                <img src={ASSETS.kymco} alt="Kymco" className="w-14 h-14 object-contain" />
              </div>
              <div>
                <div className="overline">Rivenditore Ufficiale</div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Scooter Kymco Roma</h2>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed">
              Rivenditore ufficiale Kymco per <strong>Roma Nord, Nuovo Salario e Montesacro</strong>. Vendiamo l'intera gamma Kymco — Agility, People, Downtown, AK 550, Like — con un eccellente rapporto qualità-prezzo, finanziamenti dedicati e assistenza autorizzata.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Cerchi uno <strong>scooter Kymco a Roma</strong>? Contattaci per disponibilità, prezzo e tempi di consegna. Tutti i modelli sono coperti da garanzia ufficiale.
            </p>
          </div>
        </div>
      </section>

      {/* SEO LOCAL TEXT */}
      <section className="py-20 md:py-28 bg-white" data-testid="seo-text-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overline">Roma Nord</div>
          <div className="divider-red mt-3 mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Il tuo negozio scooter nel quartiere Nuovo Salario
          </h2>
          <div className="mt-8 space-y-5 text-slate-600 leading-relaxed">
            <p>
              Il Velocipede è un <strong>negozio scooter a Roma Nord</strong> radicato nel territorio dal 1973. La nostra posizione in Piazza dell'Ateneo Salesiano, a Nuovo Salario, ci rende facilmente raggiungibili da <strong>Montesacro, Talenti, Conca d'Oro, Fidene, Settebagni</strong> e dai quartieri limitrofi.
            </p>
            <p>
              Siamo l'indirizzo di fiducia per chi cerca uno <strong>scooter Piaggio a Montesacro</strong> o uno <strong>scooter Kymco a Nuovo Salario</strong> senza muoversi dal proprio quartiere. La nostra filosofia è semplice: vendiamo scooter come se dovessimo guidarli noi. Solo modelli ufficiali, solo ricambi originali, solo assistenza autorizzata.
            </p>
            <p>
              Vuoi una <strong>consulenza personalizzata</strong>? Veniamo incontro a ogni esigenza: scooter elettrici, modelli con tre ruote MP3 per chi non ha la patente moto, maxi-scooter da turismo, scooter compatti per la città. Ti aiutiamo a scegliere il mezzo giusto in base a uso quotidiano, autonomia desiderata e budget.
            </p>
          </div>

          <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#013368] text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="font-display font-bold text-xl">Pronto a scegliere il tuo prossimo scooter?</div>
              <div className="text-slate-200 text-sm mt-1">Preventivo gratuito e senza impegno via WhatsApp.</div>
            </div>
            <a href={`https://wa.me/${BUSINESS.whatsappRaw}?text=Salve%2C%20vorrei%20un%20preventivo%20per%20uno%20scooter`} target="_blank" rel="noopener noreferrer" data-testid="scooter-bottom-cta" className="btn-wa inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm whitespace-nowrap">
              <MessageCircle className="w-4 h-4" /> Richiedi Preventivo <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
